// ======================================================================
// File: PurchaseInvoiceHeaderCard.jsx
// Module: PPE → Procurement → Purchase Invoice
//
// Purpose:
// - Purchase Invoice Header Banner
// - KPI Cards
// - Header Information
// - GRN Summary
// - Coverage Summary
// - Matching Status Display
//
// Notes:
// - Header fields editable only in DRAFT
// - KPI calculations derived from header data
// - GRN metrics displayed when available
// ======================================================================

import React from "react";

const PurchaseInvoiceHeaderCard = ({
  header = {},
  setHeader,
  isDraft,
  setDirty
}) => {

  const updateField = (field, value) => {
    if (!isDraft) return;

    setHeader((prev) => ({
      ...prev,
      [field]: value
    }));

    setDirty(true);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "DRAFT":
        return "status-draft";

      case "SUBMITTED":
        return "status-submitted";

      case "APPROVED":
        return "status-approved";

      case "POSTED":
        return "status-posted";

      default:
        return "";
    }
  };

  const getMatchingClass = (status) => {
    switch (status) {
      case "MATCHED":
        return "matching-success";

      case "PARTIALLY_MATCHED":
        return "matching-warning";

      case "EXCEPTION":
        return "matching-error";

      default:
        return "matching-pending";
    }
  };

  const coveragePercent =
    Number(header.coverage_percent || 0);

  return (
    <div className="pi-header-container">

      {/* ================================================= */}
      {/* HEADER BANNER                                    */}
      {/* ================================================= */}

      <div className="pi-header-banner">

        <div className="pi-header-left">

          <h1 className="pi-title">
            Purchase Invoice
          </h1>

          <div className="pi-number">
            {header.pi_no || "-"}
          </div>

        </div>

        <div className="pi-header-right">

          <div
            className={`status-badge ${getStatusClass(
              header.status
            )}`}
          >
            {header.status || "DRAFT"}
          </div>

          <div
            className={`matching-badge ${getMatchingClass(
              header.matching_status
            )}`}
          >
            {header.matching_status ||
              "NOT_MATCHED"}
          </div>

        </div>

      </div>

      {/* ================================================= */}
      {/* KPI ROW                                           */}
      {/* ================================================= */}

      <div className="pi-kpi-row">

        <div className="pi-kpi-card pi-kpi-total">
          <div className="pi-kpi-label">
            Material Value
          </div>
          <div className="pi-kpi-value">
            {Number(header.total_value || 0).toFixed(2)}
          </div>
        </div>

        <div className="pi-kpi-card pi-kpi-approved">
          <div className="pi-kpi-label">
            GST Value
          </div>
          <div className="pi-kpi-value">
            {Number(header.tax_value || 0).toFixed(2)}
          </div>
        </div>

        <div className="pi-kpi-card pi-kpi-draft">
          <div className="pi-kpi-label">
            Net Value
          </div>
          <div className="pi-kpi-value">
            {Number(header.net_value || 0).toFixed(2)}
          </div>
        </div>

        <div className="pi-kpi-card pi-kpi-posted">
          <div className="pi-kpi-label">
            Line Count
          </div>
          <div className="pi-kpi-value">
            {header.line_count || 0}
          </div>
        </div>

        <div className="pi-kpi-card">
          <div className="pi-kpi-label">
            Match Status
          </div>
          <div className="pi-kpi-value">
            {header.matching_status ||
              "NOT_MATCHED"}
          </div>
        </div>

      </div>

      {/* ================================================= */}
      {/* MAIN BODY                                         */}
      {/* ================================================= */}

      <div className="pi-header-grid">

        {/* ========================================= */}
        {/* HEADER INFORMATION                        */}
        {/* ========================================= */}

        <div className="pi-card">

          <div className="pi-card-title">
            Header Information
          </div>

          <div className="pi-form-grid">

            <div className="pi-form-group">
              <label>Supplier Invoice No</label>

              <input
                type="text"
                value={
                  header.supplier_invoice_no || ""
                }
                disabled={!isDraft}
                onChange={(e) =>
                  updateField(
                    "supplier_invoice_no",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="pi-form-group">
              <label>
                Supplier Invoice Date
              </label>

              <input
                type="date"
                value={
                  header.supplier_invoice_date || ""
                }
                disabled={!isDraft}
                onChange={(e) =>
                  updateField(
                    "supplier_invoice_date",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="pi-form-group">
              <label>Due Date</label>

              <input
                type="date"
                value={header.due_date || ""}
                disabled={!isDraft}
                onChange={(e) =>
                  updateField(
                    "due_date",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="pi-form-group">
              <label>Currency</label>

              <input
                type="text"
                value={
                  header.currency_code || ""
                }
                disabled
              />
            </div>

            <div className="pi-form-group">
              <label>Exchange Rate</label>

              <input
                type="text"
                value={
                  header.exchange_rate || ""
                }
                disabled
              />
            </div>

            <div className="pi-form-group">
              <label>Invoice Type</label>

              <input
                type="text"
                value={
                  header.invoice_type || ""
                }
                disabled
              />
            </div>

          </div>

          <div className="pi-form-group">
            <label>Remarks</label>

            <textarea
              rows={3}
              value={header.remarks || ""}
              disabled={!isDraft}
              onChange={(e) =>
                updateField(
                  "remarks",
                  e.target.value
                )
              }
            />
          </div>

        </div>

        {/* ========================================= */}
        {/* VENDOR INFORMATION                        */}
        {/* ========================================= */}

        <div className="pi-card">

          <div className="pi-card-title">
            Vendor Information
          </div>

          <div className="pi-summary-grid">

            <div>
              <label>Vendor</label>
              <div>
                {header.vendor_name || "-"}
              </div>
            </div>

            <div>
              <label>Vendor ID</label>
              <div>
                {header.vendor_id || "-"}
              </div>
            </div>

            <div>
              <label>Purchase Invoice No</label>
              <div>
                {header.pi_no || "-"}
              </div>
            </div>

            <div>
              <label>Purchase Invoice Date</label>
              <div>
                {header.pi_date || "-"}
              </div>
            </div>

            <div>
              <label>Status</label>
              <div>
                {header.status || "-"}
              </div>
            </div>

            <div>
              <label>Matching Status</label>
              <div>
                {header.matching_status || "-"}
              </div>
            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default PurchaseInvoiceHeaderCard;